// v1.1.0 - 2026-01-31 - Admin calculation detail page with PDF export

import { useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation } from '@/lib/hooks/useApi'
import { ti } from '@/lib/api/client'
import { PageContainer, PageHeader } from '@/components/layout/PageContainer'
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'

interface MemberDistribution {
  member_id: string
  member_name: string
  type: 'producer' | 'consumer' | 'prosumer'
  is_company: boolean
  pnrr_percent: number
  immessa_kwh: number
  prelevata_kwh: number
  eaci_kwh: number
  ti_quota: number
  eccedenza_quota: number
  costi_quota: number
  netto: number
}

interface CalculationData {
  id: string
  period: string
  tiTotal: number
  eaciTotal: number
  quotaCondivisione: number
  soglia: number
  eccedenza: number
  costiGestione: number
  residuo: number
  distribution: MemberDistribution[]
  status: 'draft' | 'approved' | 'distributed'
  notes: string | null
}

export default function CalculationDetailPage() {
  const { calcId } = useParams<{ calcId: string }>()
  const navigate = useNavigate()

  const fetchCalculation = useCallback(
    () => (calcId ? ti.get(calcId) : Promise.resolve(null)),
    [calcId]
  )

  const { data, isLoading, error, refetch } = useQuery(fetchCalculation, {
    enabled: !!calcId,
  })

  const approveMutation = useMutation((id: string) => ti.update(id, { status: 'approved' }))
  const distributeMutation = useMutation((id: string) => ti.update(id, { status: 'distributed' }))

  const calculation = data as CalculationData | null

  const handleApprove = async () => {
    if (calcId) {
      await approveMutation.mutate(calcId)
      refetch()
    }
  }

  const handleDistribute = async () => {
    if (calcId && calculation?.status === 'approved') {
      await distributeMutation.mutate(calcId)
      refetch()
    }
  }

  const handleExportPDF = async () => {
    if (!calcId) return
    try {
      const blob = await ti.downloadReport(calcId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `ti-report-${calculation?.period || 'report'}.html`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      alert((e as Error).message || 'Errore download report')
    }
  }

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value)

  const formatNumber = (value: number, decimals = 2) =>
    new Intl.NumberFormat('it-IT', { maximumFractionDigits: decimals }).format(value)

  const statusLabels: Record<string, string> = {
    draft: 'Bozza',
    approved: 'Approvato',
    distributed: 'Distribuito',
    paid: 'Pagato',
  }

  const statusColors: Record<string, 'warning' | 'success' | 'default'> = {
    draft: 'warning',
    approved: 'success',
    distributed: 'default',
    paid: 'default',
  }

  const typeLabels: Record<string, string> = {
    producer: 'Produttore',
    consumer: 'Consumatore',
    prosumer: 'Prosumer',
  }

  if (isLoading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      </PageContainer>
    )
  }

  if (error || !calculation) {
    return (
      <PageContainer>
        <PageHeader
          title="Dettaglio Calcolo"
          description="Errore nel caricamento del calcolo"
        />
        <Card>
          <CardContent className="py-8">
            <p className="text-center text-muted-foreground">
              {error || 'Calcolo non trovato'}
            </p>
            <div className="flex justify-center mt-4">
              <Button variant="outline" onClick={() => navigate('/admin/calculations')}>
                Torna ai calcoli
              </Button>
            </div>
          </CardContent>
        </Card>
      </PageContainer>
    )
  }

  const distribution = (calculation.distribution || []) as MemberDistribution[]

  return (
    <PageContainer>
      <PageHeader
        title={`Calcolo TI - ${calculation.period}`}
        description="Dettaglio distribuzione Tariffa Incentivante"
        actions={
          <Button variant="outline" onClick={() => navigate('/admin/calculations')}>
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Torna ai calcoli
          </Button>
        }
      />

      <div className="space-y-6">
        {/* Summary Cards */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Riepilogo Calcolo</CardTitle>
              <Badge variant={statusColors[calculation.status] || 'default'}>
                {statusLabels[calculation.status] || calculation.status}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground">TI Lordo</p>
                <p className="text-xl font-bold">{formatCurrency(calculation.tiTotal)}</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground">EACI Totale</p>
                <p className="text-xl font-bold">{formatNumber(calculation.eaciTotal)} kWh</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground">Quota Condivisione</p>
                <p className="text-xl font-bold">{formatNumber(calculation.quotaCondivisione)}%</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground">Soglia Pesata</p>
                <p className="text-xl font-bold">{formatNumber(calculation.soglia)}%</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Breakdown Cards */}
        <Card>
          <CardHeader>
            <CardTitle>Dettaglio Distribuzione</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 rounded-lg bg-green-50 border border-green-100">
                <p className="text-sm text-green-600">Eccedenza</p>
                <p className="text-lg font-bold text-green-700">
                  {formatCurrency(calculation.eccedenza)}
                </p>
                <p className="text-xs text-green-500 mt-1">Per consumatori non imprese</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-red-50 border border-red-100">
                <p className="text-sm text-red-600">Costi Gestione</p>
                <p className="text-lg font-bold text-red-700">
                  {formatCurrency(calculation.costiGestione)}
                </p>
                <p className="text-xs text-red-500 mt-1">IVA inclusa</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-blue-50 border border-blue-100">
                <p className="text-sm text-blue-600">Residuo</p>
                <p className="text-lg font-bold text-blue-700">
                  {formatCurrency(calculation.residuo)}
                </p>
                <p className="text-xs text-blue-500 mt-1">Per produttori e imprese</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-purple-50 border border-purple-100">
                <p className="text-sm text-purple-600">Periodo</p>
                <p className="text-lg font-bold text-purple-700">{calculation.period}</p>
                <p className="text-xs text-purple-500 mt-1">{distribution.length} membri</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Distribution Table */}
        <Card>
          <CardHeader>
            <CardTitle>Distribuzione per Membro</CardTitle>
          </CardHeader>
          <CardContent>
            {distribution.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Nessun membro nella distribuzione
              </p>
            ) : (
              <div className="rounded-md border overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-4 py-3 text-left font-medium">Membro</th>
                      <th className="px-4 py-3 text-left font-medium">Tipo</th>
                      <th className="px-4 py-3 text-right font-medium">Immessa (kWh)</th>
                      <th className="px-4 py-3 text-right font-medium">Prelevata (kWh)</th>
                      <th className="px-4 py-3 text-right font-medium">EACI (kWh)</th>
                      <th className="px-4 py-3 text-right font-medium">TI Quota</th>
                      <th className="px-4 py-3 text-right font-medium">Eccedenza</th>
                      <th className="px-4 py-3 text-right font-medium">Costi</th>
                      <th className="px-4 py-3 text-right font-medium">Netto EUR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {distribution.map((member) => (
                      <tr key={member.member_id} className="border-b hover:bg-muted/50">
                        <td className="px-4 py-3">
                          <Link
                            to={`/admin/members/${member.member_id}`}
                            className="font-medium text-primary hover:underline"
                          >
                            {member.member_name}
                          </Link>
                          <div className="flex gap-1 mt-1">
                            {member.is_company && (
                              <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                                Impresa
                              </span>
                            )}
                            {member.pnrr_percent > 0 && (
                              <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded">
                                PNRR {member.pnrr_percent}%
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="default">{typeLabels[member.type] || member.type}</Badge>
                        </td>
                        <td className="px-4 py-3 text-right font-mono">
                          {formatNumber(member.immessa_kwh)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono">
                          {formatNumber(member.prelevata_kwh)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono">
                          {formatNumber(member.eaci_kwh)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono">
                          {formatCurrency(member.ti_quota)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono">
                          {formatCurrency(member.eccedenza_quota)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-red-600">
                          {formatCurrency(member.costi_quota)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-bold text-green-600">
                          {formatCurrency(member.netto)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-muted/50 font-medium">
                      <td className="px-4 py-3" colSpan={2}>
                        Totale ({distribution.length} membri)
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {formatNumber(distribution.reduce((sum, m) => sum + m.immessa_kwh, 0))}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {formatNumber(distribution.reduce((sum, m) => sum + m.prelevata_kwh, 0))}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {formatNumber(distribution.reduce((sum, m) => sum + m.eaci_kwh, 0))}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {formatCurrency(distribution.reduce((sum, m) => sum + m.ti_quota, 0))}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {formatCurrency(distribution.reduce((sum, m) => sum + m.eccedenza_quota, 0))}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-red-600">
                        {formatCurrency(distribution.reduce((sum, m) => sum + m.costi_quota, 0))}
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-bold text-green-600">
                        {formatCurrency(distribution.reduce((sum, m) => sum + m.netto, 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </CardContent>
          <CardFooter className="flex justify-between border-t">
            <div>
              {calculation.notes && (
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium">Note:</span> {calculation.notes}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleExportPDF}>
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Export PDF
              </Button>
              {calculation.status === 'draft' && (
                <Button
                  onClick={handleApprove}
                  disabled={approveMutation.isLoading}
                >
                  {approveMutation.isLoading ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      Approvazione...
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      Approva
                    </>
                  )}
                </Button>
              )}
              {calculation.status === 'approved' && (
                <Button
                  onClick={handleDistribute}
                  disabled={distributeMutation.isLoading}
                >
                  {distributeMutation.isLoading ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      Distribuzione...
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Distribuisci
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardFooter>
        </Card>

        {/* Error messages */}
        {(approveMutation.error || distributeMutation.error) && (
          <div className="rounded-md bg-red-50 p-4 text-sm text-red-600">
            {approveMutation.error || distributeMutation.error}
          </div>
        )}
      </div>
    </PageContainer>
  )
}
